import React, { forwardRef } from 'react';
import { IOnFocusConfiguration, TFocusRetriever } from '@apia/util';
import { TModalSize } from '../types';
import { IDialogHeader } from './layout/DialogHeader';
import { WindowModal } from './WindowModal';
import { StaticModal } from './StaticModal';
import { useBreakpointIndex } from '@theme-ui/match-media';

export type TModal = {
  animationDuration?: number;
  /**
   * Aplica solamente para modales que tengan draggable=true.
   */
  defaultPosition?: {
    left?: string;
    right?: string;
    top?: string;
    bottom?: string;
  };
  /**
   * Si esta variable está en true, el modal se comportará más como una ventana
   * que como un modal
   */
  draggable?: boolean;
  children?: React.ReactNode;
  className?: string;
  id?: string;
  initialFocusConfiguration?: IOnFocusConfiguration;
  /**
   * Este retriever será pasado al método focus.on para realizar el foco en el
   * elemento inicial del modal.
   *
   * @see focus
   */
  initialFocusGetter?: (
    modalInnerRef: HTMLElement,
  ) => ReturnType<TFocusRetriever>;
  /**
   * Si se pasa un ref, el modal hará foco sobre el elemento una vez que haya
   * abierto.
   */
  initialFocusRef?: React.RefObject<unknown>;
  /**
   * Controla el estado del modal, indicando a la transición de apertura o
   * cierre que debe comenzar, dependiendo de si cambia a true o false
   * respectivamente.
   */
  isOpen?: boolean;
  maxWidth?: number | string;
  /**
   * Permite pasar un componente React que se mostrará a la derecha del título,
   * con el fin de incorporar botones en el header del modal.
   */
  NavBar?: React.ReactNode;
  noFocus?: boolean;
  noHeader?: boolean;
  /**
   * Permite definir un callback que será llamado solamente cuando el modal se
   * cierre por medio del click en el overlay, al presionar escape o mediante
   * el botón cerrar del header (La X).
   */
  onClose?: () => void;
  /**
   * Este método recibe el evento del mousedown utilizado para controllar el dragstart. Si el usuario devuelve false, el drag no será realizado.
   */
  onDragStart?: (ev: MouseEvent) => boolean;
  /**
   * Permite definir un callback que será llamado siempre que el modal se
   * cierre, una vez que la animación haya terminado.
   */
  onExited?: () => unknown;
  /**
   * Al pasar un Portal, el modal se abrirá dentro de éste y no directamente en
   * la posición actual dentro del árbol de componentes. Normalmente, este
   * Portal es pasado automáticamente por el hook useModal (No el de la
   * librería, sino el nuestro).
   *
   * @see useModal
   */
  Portal?: React.FC<{
    children: React.ReactNode;
  }>;
  /**
   * Si es true, el modal será cerrado al presionar escape.
   */
  shouldCloseOnEsc?: boolean;
  /**
   * Si es true, el modal será cerrado al hacer clic en el overlay.
   */
  shouldCloseOnOverlayClick?: boolean;
  size?: TModalSize;
  stretch?: boolean;
  title?: string;
  variant?: string;
} & Pick<IDialogHeader, 'hideCloseButton'>;

export const Modal = forwardRef(
  (props: TModal, ref: React.Ref<HTMLDivElement>) => {
    const breakpoint = useBreakpointIndex();

    if (props.draggable && breakpoint > 3) {
      return <WindowModal {...props} ref={ref} />;
    }
    return <StaticModal {...props} ref={ref} />;
  },
);

Modal.displayName = 'Modal';
